import { AvailabilityPrestationMode } from './translation_namespace/availabilities_namespace';
import {
  BOOKING_SOURCE_URL_PARAM,
  bookingSourceToUrlParam,
  type PublicBookingSource,
} from './translation_namespace/booking_source_namespace';

/**
 * Construction des liens publics `/book/[slug]`.
 *
 * Les query params portent des codes courts hérités de la V1 : p=prestations, cat=catégories,
 * l=lieux, m=membres, c=accent, bg=fond, tx=texte, r=arrondi. C'est un contrat inter-packages —
 * écrit ici, relu par `app/book/[slug]/page.tsx` et reflété en attributs par le web component
 * `<propelsia-booking>` — d'où sa place dans `@cfixe/shared` plutôt que dans une app.
 */

export type TunnelLinkAppearance = {
  accent: string;
  bg: string;
  text: string;
  radius: string;
};

export type BookingTunnelUrlOptions = {
  prestationIds?: (string | number)[];
  /**
   * ⚠️ `cat` est combiné en **ET** avec `p` côté backend : les deux clés sont posées côte à côte
   * dans le même `where` Prisma (`prestationWhere`, booking.service.ts). Ce n'est donc PAS une
   * union. Pour traduire le périmètre d'un planning — qui est, lui, une union
   * `prestation_ids ∪ prestations(catégories)` — passer par `resolveAvailabilityBookingScope`,
   * qui aplatit les catégories et n'émet jamais `cat`.
   */
  categoryIds?: (string | number)[];
  placeIds?: (string | number)[];
  memberIds?: (string | number)[];
  /**
   * Jour `YYYY-MM-DD` sur lequel ouvrir la grille de créneaux (param `d`). C'est un INDICE
   * d'ouverture, jamais un filtre : le tunnel l'ignore si la semaine est passée et avance la
   * grille de lui-même si cette semaine ne porte aucun créneau.
   */
  date?: string;
  /** Surcharge d'apparence pour ce lien précis (sinon l'apparence par défaut du pro s'applique). */
  appearance?: TunnelLinkAppearance;
  /**
   * Canal d'entrée déclaré par le lien (param `src`, statistiques de provenance). Sans lui, la
   * page `/book` enregistre « Lien direct » : inutile sur un lien partagé par le pro.
   */
  source?: PublicBookingSource;
};

/** Vrai si la chaîne est une date calendaire `YYYY-MM-DD` réelle (rejette 2026-02-31, 2026-13-01…). */
export function isValidISODate(value: string | null | undefined): boolean {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number);
  const parsed = new Date(year, month - 1, day);
  return (
    !Number.isNaN(parsed.getTime()) &&
    parsed.getFullYear() === year &&
    parsed.getMonth() === month - 1 &&
    parsed.getDate() === day
  );
}

/** Chaîne vide si l'origine ou le slug manquent : les appelants doivent traiter ce cas (jamais de `window.open('')`). */
export function buildBookingTunnelUrl(origin: string, slug: string, opts: BookingTunnelUrlOptions = {}): string {
  if (!origin || !slug) return '';

  const params = new URLSearchParams();
  const prestationIds = opts.prestationIds ?? [];
  const categoryIds = opts.categoryIds ?? [];
  const placeIds = opts.placeIds ?? [];
  const memberIds = opts.memberIds ?? [];

  if (prestationIds.length) params.set('p', prestationIds.map(String).join(','));
  if (categoryIds.length) params.set('cat', categoryIds.map(String).join(','));
  if (placeIds.length) params.set('l', placeIds.map(String).join(','));
  if (memberIds.length) params.set('m', memberIds.map(String).join(','));
  if (opts.date) params.set('d', opts.date);
  if (opts.source) params.set(BOOKING_SOURCE_URL_PARAM, bookingSourceToUrlParam(opts.source));

  if (opts.appearance) {
    params.set('c', opts.appearance.accent.replace('#', ''));
    params.set('bg', opts.appearance.bg.replace('#', ''));
    params.set('tx', opts.appearance.text.replace('#', ''));
    if (opts.appearance.radius.trim()) params.set('r', opts.appearance.radius.trim());
  }

  const qs = params.toString();
  return `${origin}/book/${slug}${qs ? `?${qs}` : ''}`;
}

/** Index catégorie → prestations, pour aplatir un périmètre `SPECIFIC` sans requête supplémentaire. */
export function groupPrestationIdsByCategoryId(
  prestations: readonly { prestation_id: number; catalog_category_id: number }[],
): Map<number, number[]> {
  const byCategory = new Map<number, number[]>();
  for (const prestation of prestations) {
    const current = byCategory.get(prestation.catalog_category_id);
    if (current) current.push(prestation.prestation_id);
    else byCategory.set(prestation.catalog_category_id, [prestation.prestation_id]);
  }
  return byCategory;
}

/**
 * Périmètre d'un planning, typé structurellement pour rester indépendant des types générés par
 * Orval (le client front est assignable tel quel).
 *
 * `pro_place` / `team_members` peuvent arriver à `null` : `transformToSchema` sérialise tout
 * tableau d'objets vide en null.
 */
export type BookingScopeAvailability = {
  prestation_mode?: string;
  prestation_ids?: number[];
  prestation_category_ids?: number[];
  pro_place?: { pro_place_id: number }[] | null;
  team_members?: { team_member_id: number }[] | null;
};

export type BookingTunnelScope = {
  prestationIds: number[];
  placeIds: number[];
  memberIds: number[];
};

/**
 * Périmètre EFFECTIF du créneau cliqué, tel que renvoyé par le calendrier agenda (surcharges de
 * créneau déjà résolues côté backend). Quand il porte un périmètre prestation, celui-ci REMPLACE
 * celui du planning — il ne le complète pas.
 */
export type BookingSlotScope = {
  prestation_mode?: string;
  prestation_ids?: number[];
  prestation_category_ids?: number[];
  pro_place_ids?: number[];
};

type PrestationScopeSource = {
  prestation_mode?: string;
  prestation_ids?: number[];
  prestation_category_ids?: number[];
};

/** Prestations couvertes par un périmètre, catégories aplaties. Vide = tout le catalogue (mode ALL). */
function resolvePrestationIds(
  source: PrestationScopeSource,
  prestationIdsByCategoryId: Map<number, number[]>,
): number[] {
  if (source.prestation_mode !== AvailabilityPrestationMode.SPECIFIC) return [];

  const ids = new Set<number>();
  for (const id of source.prestation_ids ?? []) ids.add(id);
  for (const categoryId of source.prestation_category_ids ?? []) {
    for (const id of prestationIdsByCategoryId.get(categoryId) ?? []) ids.add(id);
  }
  return [...ids];
}

/**
 * Traduit le périmètre d'un planning en filtres du tunnel.
 *
 * - `prestation_mode` ≠ SPECIFIC (donc ALL ou absent) → aucun filtre prestation = tout le catalogue.
 * - SPECIFIC → union `prestation_ids ∪ prestations(prestation_category_ids)`, **aplatie en ids de
 *   prestations**. On n'émet jamais `cat` : `p` et `cat` s'intersectent côté backend alors que le
 *   périmètre du planning est une union — les émettre ensemble donnerait presque toujours zéro
 *   prestation, donc un tunnel vide. Contrepartie assumée : le lien fige le contenu des catégories
 *   au moment où il est généré.
 *
 * `slotProPlaceIds` (lieux EFFECTIFS du créneau cliqué) prime sur les lieux du planning : une
 * surcharge de créneau les remplace et n'est pas bornée à ceux du planning.
 */
export function resolveAvailabilityBookingScope(
  availability: BookingScopeAvailability,
  prestationIdsByCategoryId: Map<number, number[]>,
  slot?: BookingSlotScope,
): BookingTunnelScope {
  // Le créneau porte déjà le périmètre EFFECTIF (gating slot-sinon-planning fait côté backend) :
  // dès qu'il annonce un mode, il fait autorité — sinon on retombe sur le planning.
  const prestationSource: PrestationScopeSource = slot?.prestation_mode !== undefined ? slot : availability;

  const planningPlaceIds = (availability.pro_place ?? []).map((place): number => place.pro_place_id);
  const slotPlaceIds = slot?.pro_place_ids ?? [];

  return {
    prestationIds: resolvePrestationIds(prestationSource, prestationIdsByCategoryId),
    placeIds: slotPlaceIds.length > 0 ? slotPlaceIds : planningPlaceIds,
    memberIds: (availability.team_members ?? []).map((member): number => member.team_member_id),
  };
}

/**
 * Périmètre SPECIFIC ne couvrant aucune prestation (planning mal configuré, ou dont toutes les
 * prestations ont été supprimées). N'émettre aucun `p` signifierait « tout le catalogue » — l'exact
 * inverse de l'intention : les appelants doivent refuser de construire le lien.
 */
export function isEmptySpecificScope(
  availability: BookingScopeAvailability,
  scope: BookingTunnelScope,
  slot?: BookingSlotScope,
): boolean {
  const effectiveMode = slot?.prestation_mode !== undefined ? slot.prestation_mode : availability.prestation_mode;
  return effectiveMode === AvailabilityPrestationMode.SPECIFIC && scope.prestationIds.length === 0;
}
